<script>
  import Chart from 'svelte-chartjs/src/Bar.svelte';
  import { External, M } from '../../external';
  import { DAY, generateCanvasGradient, calcGroupingForTimeRange, months, monthsShort } from '../../Common/tools';
  import { defaultErrorHandling, formatSecondsTimeStamp, getVideoService } from '../static';
  import { onMount } from 'svelte';
  import PeriodPicker from '../../Common/PeriodPicker.svelte';
  import Loader from '../../Common/Loader.svelte';

  export let isLoading = false;
  export let type; //VIDEO, FOLDER or USER
  export let id = {};
  export let graphData = {};
  export let chartData = initCharData();

  export let activePeriod = {
    label: M.LAST_SEVEN_DAYS,
    id: '7-days',
    from: new Date(new Date().getTime() - 6 * DAY),
    to: new Date()
  };
  let monthLabels = [];
  let dataGrouping = 'day';

  let noDataToShow = false;

  const totalViewsColor = '#FF8787';
  const engagementColor = '#3da6ff';
  const uniqueViewsColor = '#f1ee4f';
  const hoursPlayColor = '#79e161';

  const totalViewsGradientColors = ['#3ca5fe', engagementColor];
  const engagementGradientColors = ['#ff57b7', totalViewsColor];
  const uniqueViewGradientColors = [uniqueViewsColor, '#ece60e'];
  const hoursPlayedGradientColors = [hoursPlayColor, '#2c8013'];

  const graphOptions = {
    responsive: true,
    plugins: {
      legend: false,
      title: false,
      tooltip: {
        mode: 'index',
        intersect: false,
        displayColors: false
      }
    },
    scales: {
      xAxes: {
        display: false
      },
      yAxisEngagement: {
        type: 'linear',
        position: 'left',
        display: true,
        grid: { display: false },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 3,
          maxRotation: 0
        }
      },
      yAxisPlayTime: {
        type: 'linear',
        position: 'left',
        display: false
      },
      yAxisTotalViews: {
        type: 'linear',
        position: 'right',
        display: false
      },
      yAxisUniqueViews: {
        type: 'linear',
        position: 'right',
        display: false
      }
    },
    layout: {
      padding: 3
    }
  };

  let gradients;

  onMount(() => {
    gradients = getGradients();
    if (chartData.labels.length === 0) {
      getAnalytic();
    } else {
      noDataToShow = false;
      isLoading = false;
      setChart();
    }
  });

  let getAnalytic = () => {
    isLoading = true;
    let from = activePeriod.from ? getDateStr(activePeriod.from) : '';
    let to = activePeriod.to ? getDateStr(activePeriod.to) : '';
    let grouping = calcGroupingForTimeRange(null, activePeriod.from, activePeriod.to);
    //there is no hourly grouping in video analytics
    if (grouping === 'hourly' || grouping === 'daily') {
      dataGrouping = 'day';
    } else if (grouping === 'monthly') {
      dataGrouping = 'month';
    } else if (grouping === 'yearly') {
      dataGrouping = 'year';
    }
    const requestParameters = {
      from: from,
      to: to,
      allTime: activePeriod.id === 'all'
    };
    if (type === 'video' || type === 'folder') {
      requestParameters['id'] = id;
    }
    getVideoService('/analytics/' + type + '/' + dataGrouping, requestParameters)
      .then((resp) => {
        monthLabels = [];
        if (resp && resp.data && resp.data.length > 0) {
          processData(resp.data, resp.durationInSec, dataGrouping);
          noDataToShow = false;
          setChart();
        } else {
          noDataToShow = true;
        }
        isLoading = false;
      })
      .catch((err) => {
        isLoading = false;
        defaultErrorHandling();
      });
  };

  let setChart = () => {
    graphData = {
      labels: chartData.labels,
      datasets: [
        {
          label: M.TOTAL_VIEWS,
          data: chartData.totalViews.data,
          backgroundColor: gradients[1],
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.1,
          lineTension: 0.3,
          fill: true,
          borderRadius: 3,
          borderColor: 'transparent',
          order: 3,
          yAxisID: 'yAxisEngagement'
        },
        {
          label: M.ENGAGEMENT,
          data: chartData.engagement.data,
          backgroundColor: 'transparent',
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.1,
          lineTension: 0.3,
          fill: false,
          borderRadius: 3,
          borderColor: gradients[0],
          order: 2,
          type: 'line',
          yAxisID: 'yAxisTotalViews'
        } /*,
        {
          label: "Unique views",
          data: chartData.uniqueViews.data,
          backgroundColor: "transparent",
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.1,
          lineTension: 0.3,
          fill: false,
          borderRadius: 3,
          borderColor: gradients[2],
          order: 1,
          type: "line",
          yAxisID: "yAxisUniqueViews"
        },
        {
          label: "Hours played",
          data: chartData.playTime.data,
          backgroundColor: "transparent",
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.1,
          lineTension: 0.3,
          fill: false,
          borderRadius: 3,
          borderColor: gradients[3],
          order: 0,
          type: "line",
          yAxisID: "yAxisPlayTime"
        }*/
      ]
    };
  };

  function onBasicFilterChange(event) {
    activePeriod = event.detail.period;
    getAnalytic();
  }

  let processData = (data, durationInSec, grouping) => {
    chartData = initCharData();
    let flooredDuration = Math.floor(durationInSec);
    for (const value of data) {
      let l = getLabel(value.date, grouping, data.length > 7);
      chartData.labels.push(l);
      chartData.playTime.data.push(secondsToHoursStr(value.totalPlayTime));
      chartData.playTime.total += value.totalPlayTime;
      chartData.totalViews.data.push(value.totalViews);
      chartData.totalViews.total += value.totalViews;
      chartData.uniqueViews.data.push(value.uniqueViews);
      chartData.uniqueViews.total += value.uniqueViews;
      if (value.totalPlayTime === 0) {
        chartData.engagement.data.push(0);
      } else {
        let engagementValue = (value.totalPlayTime / (value.totalViews * flooredDuration)) * 100;
        chartData.engagement.data.push(engagementValue.toFixed(2));
      }
    }
    if (chartData.playTime.total === 0) {
      chartData.engagement.total = 0;
    } else {
      chartData.engagement.total = (
        (chartData.playTime.total / (chartData.totalViews.total * flooredDuration)) *
        100
      ).toFixed(2);
    }
    if (monthLabels && monthLabels.length > 0) {
      monthLabels = monthLabels;
    }
  };

  let getLabel = (label, grouping, showMonthsBelow) => {
    let date;
    switch (grouping) {
      case 'day':
        date = new Date(label);
        if (showMonthsBelow) {
          //monthLabels
          let month = months[date.getMonth()];
          if (monthLabels.length === 0 || monthLabels[monthLabels.length - 1] !== month) {
            monthLabels.push(month);
          }
          return date.getDate();
        } else {
          return External.WebPlatform.dateToStrWithMonth(date, true, false, true, monthsShort);
        }
      case 'month':
        date = new Date(label);
        return External.WebPlatform.dateToStrWithMonth(date, false, false, false, monthsShort);
      case 'year':
        date = new Date(label);
        return date.getFullYear();
    }
    return label;
  };

  let getGradients = () => {
    let canvas = document.getElementById('videoChartData').getElementsByTagName('canvas')[0];
    let firstGradient = generateCanvasGradient(canvas, totalViewsGradientColors);
    let secondGradient = generateCanvasGradient(canvas, engagementGradientColors);
    let thirdGradient = generateCanvasGradient(canvas, uniqueViewGradientColors);
    let fourthGradient = generateCanvasGradient(canvas, hoursPlayedGradientColors);
    return [firstGradient, secondGradient, thirdGradient, fourthGradient];
  };

  let initCharData = () => {
    return {
      labels: [],
      playTime: {
        data: [],
        total: 0
      },
      totalViews: {
        data: [],
        total: 0
      },
      uniqueViews: {
        data: [],
        total: 0
      },
      engagement: {
        data: [],
        total: 0
      }
    };
  };

  let secondsToHoursStr = (sec) => {
    return (sec / 3600).toFixed(2);
  };

  let getDateStr = (d) => {
    let month = '' + (d.getMonth() + 1),
      day = '' + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) {
      month = '0' + month;
    }
    if (day.length < 2) {
      day = '0' + day;
    }

    return [year, month, day].join('-');
  };
</script>

<div class="padded-crm-wrapper-outer video-analytics-wrapper" style="position: relative;">
  <!--  <div class="flex col20 f-left padded-section-analytics-warapper white-bg mb-15">-->
  <!--    <BasicFilter on:change={(event) => onBasicFilterChange(event)} period={activePeriod} />-->
  <!--  </div>-->
  <!--<div class="flex col20 f-left padded-section-analytics-warapper mb-15">
    <PeriodPicker bind:period={activePeriod} on:change={(event) => onBasicFilterChange(event)} showPeriods={['7-days', '14-days', 'month', 'all', 'custom']} />
  </div>-->
  <div class="flex col20 f-left padded-section-analytics-warapper white-bg">
    <div class="analytics-box with-right-border">
      <h3 class="flex align-center">
        {M.TOTAL_VIEWS}<span class="chart-color ml-10" style="background: {totalViewsColor}" />
      </h3>
      <h2 class="col20 f-left f-28 gray-text"><b id="videoTotalViews">{chartData.totalViews.total}</b></h2>
    </div>
    <div class="analytics-box with-right-border">
      <h3 class="flex align-center">
        {M.ENGAGEMENT}<span class="chart-color ml-10" style="background: {engagementColor}" />
        <span class="material-icons-outlined stats-info" style="font-size: 20px;">
          info
          <span class="none-soft hidden-info">
            <h4>{M.ENGAGEMENT}</h4>
            <span>{M.ENGAGEMENT_DESC}</span>
          </span>
        </span>
      </h3>
      <h2 class="col20 f-left f-28 gray-text"><b id="videoEngagement">{chartData.engagement.total}%</b></h2>
    </div>
    <div class="analytics-box with-right-border">
      <h3 class="flex align-center">
        {M.UNIQUE_VIEWS}<span class="chart-color ml-10 none" style="background: {uniqueViewsColor}" />
      </h3>
      <h2 class="col20 f-left f-28 gray-text"><b id="videoUniqueViews">{chartData.uniqueViews.total}</b></h2>
    </div>
    <div class="analytics-box">
      <h3 class="flex align-center">
        {M.HOURS_PLAYED} <span class="chart-color ml-10 none" style="background: {hoursPlayColor}" />
      </h3>
      <h2 class="col20 f-left f-28 gray-text">
        <b id="videoHoursPlayed">{secondsToHoursStr(chartData.playTime.total)}</b>
      </h2>
    </div>
  </div>

  <div class="col20 f-left video-analytics-date-picker flex align-center justify-center">
    <PeriodPicker
      bind:period={activePeriod}
      on:change={(event) => onBasicFilterChange(event)}
      showPeriods={['7-days', '14-days', 'month', 'all', 'custom']} />
  </div>
  <div class="col20 f-left relative mt-15 modern-chart-box">
    {#if isLoading}
      <Loader />
    {/if}
    {#if noDataToShow}
      <h3>{M.NO_DATA_TO_SHOW}</h3>
    {/if}
    <div id="videoChartData">
      {#key graphData}
        <Chart data={graphData} options={graphOptions} />
      {/key}
    </div>
    <div class="axis">
      {#each chartData.labels as label, i}
        <div class="tick">
          <span class="tick-label">{label}</span>
          <span class="value value-first"
            ><span class="chart-color" style="background: {totalViewsColor}" />{chartData.totalViews.data[i]}</span>
          <span class="value value-second"
            ><span class="chart-color" style="background: {engagementColor}" />{chartData.engagement.data[i]}%</span>
          <span class="value value-third"
            ><span class="chart-color" style="background: {uniqueViewsColor}" />{chartData.uniqueViews.data[i]}</span>
          <span class="value value-fourth"
            ><span class="chart-color" style="background: {hoursPlayColor}" />{chartData.playTime.data[i]}</span>
        </div>
      {/each}
    </div>
    {#if dataGrouping === 'day'}
      <div class="col20 f-left flex flex-row chart-label-row">
        {#each monthLabels as label, i}
          <span
            class="flex-1"
            class:ta-left={i === 0 && monthLabels.length > 1}
            class:ta-right={i === monthLabels.length - 1 && monthLabels.length > 1}
            class:ta-center={monthLabels.length === 1 || (i > 1 && i < monthLabels.length - 1)}>
            <p>{label}</p>
          </span>
        {/each}
      </div>
    {/if}
  </div>
</div>
